/**
 * Tick boxes on the review screen.
 *
 * These groups used to have no control at all. They travelled in the capture
 * payload as a raw `{group: [option, ...]}` map that nothing rendered, while
 * the reserved `__checkbox__<group>` rows the pipeline writes into the
 * extraction table leaked out as ordinary text fields — so the one way a
 * receptionist could change a tick was to edit a comma-joined string in a box
 * labelled `__checkbox__card_type`.
 *
 * On the registration card they were worse than absent. `config.ini` described
 * Mr/Mrs/Miss and the six payment methods as *fields* with a point to draw an
 * "X" at, so the converted template had nine text fields the scanner could
 * never read, listed on the review screen among the things it had failed to
 * read. They are checkboxes now, and they sit where the card puts them rather
 * than in a heap at the end — see `FieldDeck`.
 */

import type { CaptureCheckboxGroup } from '@/lib/types'
import { cn } from '@/lib/utils'

export function CheckboxGroupField({
  group,
  onChange,
}: {
  group: CaptureCheckboxGroup
  onChange: (groupKey: string, options: string[]) => void
}) {
  const selected = group.options.filter((o) => o.selected).map((o) => o.option_key)

  return (
    <fieldset className="rounded-[10px] border border-[var(--border)] p-2.5">
      <legend className="px-1 text-[12px] font-medium leading-5 text-[var(--text)]">
        {group.label}
        <span className="ms-1.5 font-normal text-[var(--text-muted)]">
          {group.multi_select ? 'any that apply' : 'pick one'}
        </span>
      </legend>
      {/* `min-h-9` and `mt-1.5`: the same rhythm as a field card - label line,
          6px, a 36px control - so a group sitting beside a field ends on the
          same edge instead of eight pixels short of it. */}
      <div
        className="mt-1.5 flex flex-wrap gap-1.5"
        role={group.multi_select ? 'group' : 'radiogroup'}
      >
        {group.options.map((option) => (
          <button
            key={option.option_key}
            type="button"
            role={group.multi_select ? 'checkbox' : 'radio'}
            aria-checked={option.selected}
            onClick={() =>
              onChange(
                group.group_key,
                option.selected
                  ? selected.filter((key) => key !== option.option_key)
                  : group.multi_select
                    ? [...selected, option.option_key]
                    : [option.option_key],
              )
            }
            className={cn(
              'min-h-9 rounded-[8px] border px-2.5 text-[12px] transition-colors',
              option.selected
                ? 'border-[var(--brand)] bg-[var(--brand)] text-[var(--on-brand)]'
                : 'border-[var(--border)] bg-[var(--surface)] hover:border-[var(--brand)]',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  )
}

/** Every group, stacked. Used where there is no document order to follow. */
export function CheckboxGroups({
  groups,
  onChange,
}: {
  groups: CaptureCheckboxGroup[]
  onChange: (groupKey: string, options: string[]) => void
}) {
  return (
    <>
      {groups.map((group) => (
        <CheckboxGroupField key={group.group_key} group={group} onChange={onChange} />
      ))}
    </>
  )
}
